import { useLazyQuery } from "@apollo/client";
import AIInputView from "ai/AIInputView";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Input, Select } from "components/base/Form";
import InputSelect from "components/base/InputSelect";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import ModalProductSelector from "components/shared/ModalProductSelector";
import useFactories from "hooks/useFactories";
import { useModals } from "ModalProvider";
import {
  FETCH_PRODUCT_DETAILS,
  FETCH_PURCHASE_PRODUCTS,
  FETCH_SALE_PRODUCTS,
} from "pages/development/projects/graphql";
import { Fragment } from "react";
import { BsStars, BsTrash } from "react-icons/bs";
import { v4 } from "uuid";
import FactorySelector from "./FactorySelector";
import SortItemsView from "./SortComponentsView";

const COST_COMPANY = "AB";
const DEFAULT_CURRENCY = "CNY";

const ComponentsView = ({
  components,
  onChange,
  fixedComponentQty,
  supplierRequired,
  readOnly,
}) => {
  const [fetchProductDetails, fetchProductDetailsRes] = useLazyQuery(
    FETCH_PRODUCT_DETAILS,
  );
  const { loading, error, allFactories } = useFactories();

  const modal = useModals();
  const aiModal = useModals();

  function aiInputHandler() {
    aiModal.present({
      title: "Add with AI",
      children: (
        <AIInputView
          type="SUPPLIER-PRICES"
          onCompleted={(res) => aiAnalysisCompleteHandler(res)}
        />
      ),
    });
  }

  function aiAnalysisCompleteHandler(res) {
    aiModal.hide();
    const parsedRes = JSON.parse(res);
    const newComponents = parsedRes.map((i) => ({
      ...i,
      qty: 1,
      currency: "CNY",
    }));
    onChange({ components: [...components, ...newComponents] });
  }

  function addComponent() {
    const newComponent = {
      id: v4(),
      name: "New Component",
      qty: 1,
      prices: [{ minQty: 1, price: 0, currency: DEFAULT_CURRENCY }],
      type: "Product",
      currency: DEFAULT_CURRENCY,
    };
    onChange({ components: [...components, newComponent] });
  }

  function duplicateComponent(sourceComponent) {
    let sourceIndex = components.findIndex(
      (component) => component.id === sourceComponent.id,
    );

    if (sourceIndex < 0) {
      sourceIndex = components.findIndex(
        (component) => component === sourceComponent,
      );
    }

    if (sourceIndex < 0) {
      return;
    }

    const duplicatedComponent = {
      ...sourceComponent,
      id: v4(),
      name: `${sourceComponent.name} (Duplicated)`,
      prices: sourceComponent.prices.map((price) => ({ ...price })),
    };

    const newComponents = [
      ...components.slice(0, sourceIndex + 1),
      duplicatedComponent,
      ...components.slice(sourceIndex + 1),
    ];

    onChange({ components: newComponents });
  }

  function deleteComponent(compIndex) {
    if (window.confirm("Are you sure to delete this component?") === false) {
      return;
    }
    const newComponents = components.filter((_, i) => i !== compIndex);
    onChange({ components: newComponents });
  }

  function buildComponentPrices(activePrices) {
    const firstPriceCurrency = activePrices[0]?.currency || DEFAULT_CURRENCY;
    const companyPrices = activePrices.filter(
      (p) => p.company === COST_COMPANY,
    );
    const supplier = companyPrices.find((p) => p.supplier)?.supplier || null;
    const selectedSupplierPrices = supplier
      ? companyPrices.filter((p) => p.supplier?.id === supplier.id)
      : companyPrices;

    const prices = selectedSupplierPrices.map((p) => ({
      minQty: p.minQty,
      price: p.price,
    }));

    if (prices.length > 0) {
      return {
        prices,
        supplier,
        currency: selectedSupplierPrices[0]?.currency || firstPriceCurrency,
      };
    }

    return {
      prices: [{ minQty: 1, price: 0 }],
      supplier: null,
      currency: firstPriceCurrency,
    };
  }

  function buildImportedComponent({ name, qty, activePrices }) {
    const { prices, supplier, currency } = buildComponentPrices(activePrices);
    const newComponent = {
      id: v4(),
      name,
      qty,
      prices,
      type: "Product",
      currency,
    };

    if (supplier) {
      newComponent.supplierId = supplier.id;
      newComponent.supplierName = supplier.name;
    }

    return newComponent;
  }

  function showComponents() {
    modal.present({
      title: "Choose Component",
      children: (
        <ModalProductSelector
          query={FETCH_PURCHASE_PRODUCTS}
          onSelect={selectComponent}
        />
      ),
    });
  }

  function showProduct() {
    modal.present({
      title: "Choose Product",
      children: (
        <ModalProductSelector
          query={FETCH_SALE_PRODUCTS}
          onSelect={selectSaleProduct}
        />
      ),
    });
  }

  function selectComponent(product) {
    modal.hide();
    fetchProductDetails({ variables: { id: product.id } }).then((res) => {
      const productDetails = res.data.product;

      const newComponent = buildImportedComponent({
        name: productDetails.name,
        qty: 1,
        activePrices: productDetails.activePrices,
      });

      onChange({
        components: [...components, newComponent],
      });
    });
  }

  function selectSaleProduct(product) {
    modal.hide();
    fetchProductDetails({ variables: { id: product.id } }).then((res) => {
      const productDetails = res.data.product;

      const newComponents = productDetails.children.map((i) =>
        buildImportedComponent({
          name: i.product.name,
          qty: fixedComponentQty ? 1 : i.qty,
          activePrices: i.product.activePrices,
        }),
      );

      onChange({
        components: [...components, ...newComponents],
        orderQty: productDetails.defaultQty,
      });
    });
  }

  function addPrice(compIndex, priceIndex) {
    // Add a new price entry after the current one
    const newPrices = [
      ...components[compIndex].prices.slice(0, priceIndex + 1),
      { minQty: 1, price: 0 },
      ...components[compIndex].prices.slice(priceIndex + 1),
    ];
    const newComponents = components.map((c, i) =>
      i === compIndex ? { ...c, prices: newPrices } : c,
    );
    onChange({ components: newComponents });
  }

  function removePrice(compIndex, priceIndex) {
    if (components[compIndex].prices.length <= 1) {
      return;
    }

    if (window.confirm("Are you sure to delete this price entry?") === false) {
      return;
    }

    const newPrices = components[compIndex].prices.filter(
      (_, i) => i !== priceIndex,
    );
    const newComponents = components.map((c, i) =>
      i === compIndex ? { ...c, prices: newPrices } : c,
    );
    onChange({ components: newComponents });
  }

  function clearComponentPrices() {
    if (window.confirm("Are you sure to clear all components?") === false) {
      return;
    }
    onChange({ components: [] });
  }

  function sortComponents() {
    modal.present({
      title: "Sort Components",
      children: (
        <SortItemsView
          originalItems={components}
          onSave={(newComponents) => {
            Alert("success", "Components sorted.");
            onChange({ components: newComponents });
            modal.hide();
          }}
          rowView={(i) => (
            <div className="">
              {i.type === "Product" ? "⚽️" : "📦"} {i.name}
            </div>
          )}
        />
      ),
    });
  }

  if (loading)
    return (
      <div>
        <InlineSpinner />
      </div>
    );
  if (error) return <Errors error={error} />;

  return (
    <div>
      <div className="space-y-4">
        {components.length > 0 && (
          <div className="-mx-2">
            <table>
              <thead>
                <tr>
                  <th className="pt-0">
                    <div className="flex items-center space-x-3">
                      <div>Component</div>
                      <div>
                        <Button onClick={sortComponents}>↕ Sort</Button>
                      </div>
                    </div>
                  </th>
                  {!fixedComponentQty && (
                    <th className="pt-0 text-right">qty / ctn</th>
                  )}
                  <th className="pt-0 text-right">QTY Levels</th>
                  <th className="pt-0 text-right">Price</th>
                  {!readOnly && <th></th>}
                </tr>
              </thead>
              <tbody>
                {components.map((component, compIndex) => (
                  <Fragment key={compIndex}>
                    <tr key={compIndex}>
                      <td
                        rowSpan={component.prices.length + 1}
                        className="w-full border-y border-r dark:border-gray-700"
                      >
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <div>#{compIndex + 1}</div>
                            <div className="flex items-center space-x-1">
                              <Button
                                duplicate
                                onClick={() => duplicateComponent(component)}
                              ></Button>
                              <Button
                                trash
                                onClick={() => deleteComponent(compIndex)}
                              ></Button>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Select
                              className="flex-shrink-0"
                              value={component.type}
                              onChange={(e) => {
                                const newComponents = components.map((c, i) =>
                                  i === compIndex
                                    ? { ...c, type: e.target.value }
                                    : c,
                                );
                                onChange({ components: newComponents });
                              }}
                              disabled={readOnly}
                            >
                              <option value="Product">⚽️</option>
                              <option value="Packaging">📦</option>
                            </Select>
                            <Input
                              value={component.name}
                              className="w-full"
                              onChange={(e) => {
                                const newComponents = components.map((c, i) =>
                                  i === compIndex
                                    ? { ...c, name: e.target.value }
                                    : c,
                                );
                                onChange({ components: newComponents });
                              }}
                              disabled={readOnly}
                            />
                          </div>

                          <div className="flex items-center space-x-2">
                            <Select
                              className="shrink-0"
                              value={component.currency}
                              onChange={(e) => {
                                const newComponents = components.map((c, i) =>
                                  i === compIndex
                                    ? { ...c, currency: e.target.value }
                                    : c,
                                );
                                onChange({ components: newComponents });
                              }}
                              disabled={readOnly}
                            >
                              <option value="CNY">CNY</option>
                              <option value="USD">USD</option>
                            </Select>

                            {supplierRequired ? (
                              <FactorySelector
                                className="w-full"
                                value={component.supplierId}
                                disabled={readOnly}
                                onChange={(value) => {
                                  const newComponents = components.map(
                                    (c, i) =>
                                      i === compIndex
                                        ? { ...c, supplierId: value }
                                        : c,
                                  );
                                  onChange({ components: newComponents });
                                }}
                              />
                            ) : (
                              <InputSelect
                                placeholder="Supplier"
                                className="w-full"
                                value={component.supplierName}
                                disabled={readOnly}
                                options={allFactories}
                                onChange={(value) => {
                                  const newComponents = components.map(
                                    (c, i) =>
                                      i === compIndex
                                        ? { ...c, supplierName: value }
                                        : c,
                                  );
                                  onChange({ components: newComponents });
                                }}
                                onSelect={(supplier) => {
                                  // If choose, set supplierId
                                  const newComponents = components.map(
                                    (c, i) =>
                                      i === compIndex
                                        ? {
                                            ...c,
                                            supplierName: supplier.name,
                                            supplierId: supplier.id,
                                          }
                                        : c,
                                  );
                                  onChange({ components: newComponents });
                                }}
                              />
                            )}
                          </div>
                          <div>
                            <Input
                              className="w-full"
                              placeholder="Notes"
                              value={component.notes}
                              onChange={(e) => {
                                const newComponents = components.map((c, i) =>
                                  i === compIndex
                                    ? { ...c, notes: e.target.value }
                                    : c,
                                );
                                onChange({ components: newComponents });
                              }}
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                      </td>

                      {!fixedComponentQty && (
                        <td
                          rowSpan={component.prices.length + 1}
                          className="w-full border-y border-r dark:border-gray-700"
                        >
                          <div className="flex items-center space-x-2">
                            <Input
                              value={component.qty}
                              className="w-16 text-center"
                              onChange={(e) => {
                                const newComponents = components.map((c, i) =>
                                  i === compIndex
                                    ? { ...c, qty: e.target.value }
                                    : c,
                                );
                                onChange({ components: newComponents });
                              }}
                              disabled={readOnly}
                            />
                          </div>
                        </td>
                      )}
                    </tr>

                    {component.prices.map((price, priceIndex) => (
                      <tr
                        key={priceIndex}
                        className={`
                          ${priceIndex === 0 ? "border-t dark:border-gray-700" : ""}
                          ${priceIndex === component.prices.length - 1 ? "border-b dark:border-gray-700" : ""}
                        `}
                      >
                        <td
                          className={`
                              ${
                                component.prices.length > 1
                                  ? priceIndex > 0
                                    ? "pt-0"
                                    : ""
                                  : ""
                              }
                              `}
                        >
                          <Input
                            type="number"
                            value={price.minQty}
                            className="w-24 text-right"
                            onChange={(e) => {
                              const newPrices = component.prices.map((p, i) =>
                                i === priceIndex
                                  ? { ...p, minQty: Number(e.target.value) }
                                  : p,
                              );
                              const newComponents = components.map((c, i) =>
                                i === compIndex
                                  ? { ...c, prices: newPrices }
                                  : c,
                              );
                              onChange({ components: newComponents });
                            }}
                            disabled={readOnly}
                          />
                        </td>
                        <td
                          className={`
                              ${
                                component.prices.length > 1
                                  ? priceIndex > 0
                                    ? "pt-0"
                                    : ""
                                  : ""
                              }
                            `}
                        >
                          <Input
                            type="number"
                            value={price.price}
                            disabled={readOnly}
                            className={`w-24 text-right`}
                            onChange={(e) => {
                              const newPrices = component.prices.map((p, i) =>
                                i === priceIndex
                                  ? { ...p, price: Number(e.target.value) }
                                  : p,
                              );
                              const newComponents = components.map((c, i) =>
                                i === compIndex
                                  ? { ...c, prices: newPrices }
                                  : c,
                              );
                              onChange({ components: newComponents });
                            }}
                          />
                        </td>
                        {!readOnly && (
                          <td className="pl-3 p-0">
                            <div className="flex items-center space-x-2">
                              <Button
                                className="-translate-y-[1px]"
                                onClick={() => addPrice(compIndex, priceIndex)}
                              >
                                +
                              </Button>
                              {component.prices.length > 1 && (
                                <Button
                                  danger
                                  onClick={() =>
                                    removePrice(compIndex, priceIndex)
                                  }
                                >
                                  <BsTrash />
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!readOnly && (
          <div>
            <div className="opacity-70 text-xs">
              You can add a new component, or choosing from existing component.
              Choosing a product will add all its components to the list.
            </div>
            <div className="text-xs items-center flex space-x-4 mt-1">
              <Button onClick={addComponent}>+ Add Component</Button>
              <Button onClick={aiInputHandler} icon={<BsStars />}>
                Add with AI
              </Button>
              <Button
                onClick={showComponents}
                loading={fetchProductDetailsRes.loading}
              >
                Choose Component
              </Button>
              <Button
                onClick={showProduct}
                loading={fetchProductDetailsRes.loading}
              >
                Choose Product
              </Button>
              {components.length > 0 && (
                <Button onClick={clearComponentPrices} danger>
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentsView;
